import * as React from "react";

import { definedMap } from "@glideapps/ts-necessities";

import { asString, asArrayValue, isLoadedDefined, loadedDefinedMap, asDate } from "common/components/component-helpers";
import type {
    PotentialValue,
    ForceValueProducer,
    AppOther,
    AppEnvironment,
    BasicUserProfile,
    Value} from "common/components/types";
import {
    LOADING,
    ActionSource
} from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { Data } from "common/components/data";
import type { CommentData as Comment} from "../ui/smart/comments/comments";
import Comments, { CommentsSkeleton } from "../ui/smart/comments/comments";
import { documentIDColumnName } from "common/Database";
import { mapFilterUndefined } from "common/Support";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";

export function forceComments(comments: Value, force: ForceValueProducer): readonly Comment[] {
    const processedComments: Comment[] = [];
    for (const commentVP of asArrayValue(comments)) {
        const inspector = commentVP.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) continue;
        const username = loadedDefinedMap(definedMap(inspector.getProperty("username"), force), asString);
        const email = loadedDefinedMap(definedMap(inspector.getProperty("email"), force), asString);
        const dateTime = loadedDefinedMap(definedMap(inspector.getProperty("dateTime"), force), asDate);
        const commentText = loadedDefinedMap(definedMap(inspector.getProperty("comment"), force), asString);
        const documentID = loadedDefinedMap(definedMap(inspector.getProperty(documentIDColumnName), force), asString);
        if (username === undefined || dateTime === undefined || commentText === undefined || documentID === undefined) {
            continue;
        }
        processedComments.push({
            username,
            email,
            dateTime,
            comment: commentText,
            documentID,
        });
    }

    return processedComments;
}

export function forceUserProfiles(
    userProfiles: PotentialValue,
    force: ForceValueProducer
): readonly BasicUserProfile[] | undefined {
    if (userProfiles === undefined) return undefined;

    return mapFilterUndefined(loadedDefinedMap(userProfiles, asArrayValue) ?? [], userVP => {
        const inspector = userVP.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return undefined;
        const email = loadedDefinedMap(definedMap(inspector.getProperty("email"), force), asString);
        if (email === undefined) return undefined;
        const name = loadedDefinedMap(definedMap(inspector.getProperty("name"), force), asString);
        const image = loadedDefinedMap(definedMap(inspector.getProperty("image"), force), asString);
        return { email, name, image };
    });
}

export const CommentsMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[comments, onPost, onTapNew, onDelete, topic, userProfiles]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );
    const { onClickWithCustomContext: onClickDelete } = useOnClickFromAction(
        onDelete,
        ActionSource.DeleteComments,
        ctx,
        env
    );
    const { onClick: onTap } = useOnClickFromAction(onTapNew, ActionSource.Note, ctx, env);
    const { onClickWithCustomContext: onClickPost } = useOnClickFromAction(onPost, ActionSource.PostComment, ctx, env);
    const onPostComment = React.useCallback(
        async (username: string, email: string | undefined, comment: string) => {
            if (onClickPost === undefined) {
                return;
            }
            await onClickPost(Data.fromJSON<AppOther, AppEnvironment>({ username, email, comment }), undefined);
        },
        [onClickPost]
    );
    if (comments === LOADING) {
        return <CommentsSkeleton appKind={env.appEnvironment.appKind} />;
    }
    if (comments === undefined) return null;
    const processedComments = forceComments(comments, force);

    const profiles = forceUserProfiles(userProfiles, force);

    let deleteComments: ((documentIDs: ReadonlyArray<string>) => void) | undefined;
    if (onClickDelete !== undefined && env.appEnvironment.isBuilder) {
        deleteComments = async documentIDs => {
            await onClickDelete(Data.fromJSON<AppOther, AppEnvironment>(documentIDs), undefined);
        };
    }

    return (
        <Comments
            appEnvironment={env.appEnvironment}
            renderEnvironment={env}
            comments={processedComments}
            postComment={onPostComment}
            onTapNew={onTap !== undefined ? () => onTap(undefined) : undefined}
            deleteComments={deleteComments}
            topic={loadedDefinedMap(topic, asString)}
            userProfiles={profiles}
        />
    );
};
